// src/components/Search/S1DownloadLinksDisplay.js

import React, { useMemo } from 'react';
import { parseSeasonEpisode } from '../../utils/s1UrlParser';
import { getResolution } from '../../utils/qualityUtils';
import { FaFilm, FaTv, FaDownload } from 'react-icons/fa'; // Removed FaArrowUp

export default function DownloadLinksDisplay({ links }) {
  const { movies, tvShows } = useMemo(() => {
    if (!Array.isArray(links)) return { movies: [], tvShows: [] }; // Ensure links is an array

    const categorized = {
      movies: [],
      tvShows: [],
    };

    links.forEach((link) => {
      if (link.url.includes('/Movies/')) {
        categorized.movies.push(link);
      } else if (link.url.includes('/Series/')) {
        categorized.tvShows.push(link);
      }
    });

    categorized.movies.sort((a, b) => a.title.localeCompare(b.title));
    categorized.tvShows.sort((a, b) => {
      const { season: seasonA, episode: episodeA } = parseSeasonEpisode(a.url);
      const { season: seasonB, episode: episodeB } = parseSeasonEpisode(b.url);
      const seasonNumA = seasonA ? parseInt(seasonA.replace('S', ''), 10) : 0;
      const episodeNumA = episodeA ? parseInt(episodeA.replace('E', ''), 10) : 0;
      const seasonNumB = seasonB ? parseInt(seasonB.replace('S', ''), 10) : 0;
      const episodeNumB = episodeB ? parseInt(episodeB.replace('E', ''), 10) : 0;

      return seasonNumA !== seasonNumB ? seasonNumA - seasonNumB : episodeNumA - episodeNumB;
    });

    return categorized;
  }, [links]);

  // Removed scrollToTop and associated button

  // Helper function to render download buttons
  const renderDownloadButton = (link, type) => {
    const { season, episode } = parseSeasonEpisode(link.url);
    const titleWithSeasonEpisode = season && episode
      ? `${link.title} ${season}${episode}`
      : link.title;
    const displayText = `${link.quality}`;
    const resolution = getResolution(link.quality);

    // Determine badge color based on resolution
    const badgeColor = resolution >= 1080
      ? 'bg-green-500'
      : resolution >= 720
      ? 'bg-yellow-500'
      : 'bg-red-500';

    // Choose icon based on type
    const TypeIcon = type === 'movie' ? FaFilm : FaTv;

    return (
      <a
        key={link.url}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-gray-900 p-6 rounded-xl shadow-lg hover:shadow-2xl transition transform hover:scale-105 duration-300 flex flex-col justify-between"
        aria-label={`Download ${titleWithSeasonEpisode} at ${displayText}`}
      >
        {/* Title Row */}
        <div className="flex items-center space-x-3">
          <TypeIcon className="text-accent text-2xl" />
          <h4 className="text-xl font-semibold text-white">{titleWithSeasonEpisode}</h4>
        </div>

        {/* Quality Row */}
        <div className="mt-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeColor} text-white`}>
            {displayText}
          </span>
        </div>

        {/* Download Icon */}
        <div className="mt-4 flex justify-end">
          <FaDownload className="text-accent w-6 h-6" />
        </div>
      </a>
    );
  };

  return (
    <div className="p-6 w-full max-w-6xl mx-auto relative">
      <h3 className="text-3xl font-bold mb-6">Available Downloads</h3>

      {/* Movies Section */}
      {movies.length > 0 && (
        <div className="mb-8">
          <h4 className="text-2xl font-semibold text-accent mb-4">Movies</h4>
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {movies.map((link) => renderDownloadButton(link, 'movie'))}
          </div>
        </div>
      )}

      {/* TV Shows Section */}
      {tvShows.length > 0 && (
        <div>
          <h4 className="text-2xl font-semibold text-accent mb-4">TV Shows</h4>
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {tvShows.map((link) => renderDownloadButton(link, 'tv'))}
          </div>
        </div>
      )}
    </div>
  );
}
